import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Search, RefreshCw, AlertCircle, CheckCircle, Clock, FileText, MapPin, Calendar, DollarSign, Filter, Eye, Plus, Send, X, MoreVertical, Edit, Trash2, Grid, List, FolderOpen, Rocket, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { adminAPI, clientAPI, vehiclesAPI } from '@/services/api';
import DataTable from '@/components/ui/data-table';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';

const RequestManagement = ({ clientId, onStatisticsUpdate }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [statistics, setStatistics] = useState({
    total: 0,
    published: 0,
    completed: 0,
    cancelled: 0,
    draft: 0,
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [creatingRequest, setCreatingRequest] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loadingVehicleTypes, setLoadingVehicleTypes] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRequestForAction, setSelectedRequestForAction] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'
  const [requestData, setRequestData] = useState({
    from_location_id: null,
    to_location_id: null,
    from_location_name: '',
    to_location_name: '',
    preferred_pickup_date: '',
    preferred_delivery_date: '',
    declared_value: '',
    payment_terms: 'PREPAID',
    urgency_level: 'NORMAL',
    description: '',
    preferred_vehicle_type: null,
  });

  // Charger les données initiales
  const loadInitialData = useCallback(async () => {
    if (!clientId) {
      console.warn('RequestManagement: clientId manquant');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Par défaut, charger toutes les demandes (pas de filtre de statut)
      const params = {};
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (urgencyFilter && urgencyFilter !== 'all') {
        params.urgency_level = urgencyFilter;
      }

      const response = await adminAPI.getClientRequests(clientId, params);
      const requestsData = response.data.results || response.data || [];

      setRequests(requestsData);

      // Calculer les statistiques
      const stats = {
        total: requestsData.length,
        published: requestsData.filter(r => r.status === 'PUBLISHED').length,
        completed: requestsData.filter(r => r.status === 'COMPLETED').length,
        cancelled: requestsData.filter(r => r.status === 'CANCELLED').length,
        draft: requestsData.filter(r => r.status === 'DRAFT').length,
      };
      setStatistics(stats);

      if (onStatisticsUpdate) {
        onStatisticsUpdate(stats);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des demandes:', error);
      console.error('Détails de l\'erreur:', error.response?.data);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de charger les demandes de transport',
        variant: 'destructive',
      });
      setRequests([]);
      setStatistics({
        total: 0,
        published: 0,
        completed: 0,
        cancelled: 0,
        draft: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [clientId, statusFilter, urgencyFilter, onStatisticsUpdate]);

  // Charger les données au montage et quand les filtres changent
  useEffect(() => {
    if (clientId) {
      loadInitialData();
    }
  }, [clientId, loadInitialData]);

  // Fonction pour grouper les locations par ville (éviter les doublons)
  const groupLocationsByCity = (locations) => {
    const cityMap = new Map();

    locations.forEach((location) => {
      const cityName = (location.city || location.label || '').toLowerCase().trim();

      if (cityName) {
        if (!cityMap.has(cityName)) {
          cityMap.set(cityName, location);
        } else {
          const existing = cityMap.get(cityName);
          const existingLabel = (existing.label || existing.city || '').trim();
          const newLabel = (location.label || location.city || '').trim();
          if (newLabel.length > existingLabel.length) {
            cityMap.set(cityName, location);
          }
        }
      }
    });

    return Array.from(cityMap.values());
  };

  // Charger les locations disponibles
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const response = await clientAPI.getLocations({});
        const allLocations = response.data.results || response.data || [];
        const groupedLocations = groupLocationsByCity(allLocations);
        setLocations(groupedLocations);
      } catch (err) {
        console.error('Erreur lors du chargement des locations:', err);
      } finally {
        setLoadingLocations(false);
      }
    };
    fetchLocations();
  }, []);

  // Charger les types de véhicules disponibles
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        setLoadingVehicleTypes(true);
        const response = await vehiclesAPI.getVehicleTypes();
        setVehicleTypes(response.data.results || response.data || []);
      } catch (err) {
        console.error('Erreur lors du chargement des types de véhicules:', err);
      } finally {
        setLoadingVehicleTypes(false);
      }
    };
    fetchVehicleTypes();
  }, []);

  // Gestion de la recherche
  const handleSearch = (value) => {
    setSearchTerm(value);
    // Filtrer localement pour l'instant
  };

  // Rafraîchir les données
  const handleRefresh = async () => {
    await loadInitialData();
    toast({
      title: 'Succès',
      description: 'Données mises à jour',
    });
  };

  // Créer une demande au nom du client
  const handleCreateRequest = async () => {
    const hasFromLocation = requestData.from_location_id && requestData.from_location_id !== 'new';
    const hasFromLocationName = requestData.from_location_name && requestData.from_location_name.trim() !== '';
    const hasToLocation = requestData.to_location_id && requestData.to_location_id !== 'new';
    const hasToLocationName = requestData.to_location_name && requestData.to_location_name.trim() !== '';

    if ((!hasFromLocation && !hasFromLocationName) || (!hasToLocation && !hasToLocationName)) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner ou saisir les lieux de départ et d\'arrivée.',
        variant: 'destructive',
      });
      return;
    }

    let fromLocationId = hasFromLocation ? requestData.from_location_id : null;
    let toLocationId = hasToLocation ? requestData.to_location_id : null;

    // Créer ou trouver le lieu de départ
    if (hasFromLocationName && !fromLocationId) {
      try {
        const response = await clientAPI.createLocation({
          city: requestData.from_location_name,
          country: 'Guinée',
        });
        fromLocationId = response.data.id;
      } catch (err) {
        if (err.response?.data?.existing_location) {
          const existingLocation = err.response.data.existing_location;
          toast({
            title: 'Ville existante détectée',
            description: `La ville "${existingLocation.city}" existe déjà. Veuillez la sélectionner dans la liste déroulante.`,
            variant: 'default',
          });
          const response = await clientAPI.getLocations({});
          const allLocations = response.data.results || response.data || [];
          const groupedLocations = groupLocationsByCity(allLocations);
          setLocations(groupedLocations);
          setRequestData(prev => ({
            ...prev,
            from_location_id: existingLocation.id,
            from_location_name: '',
          }));
          return;
        }
        try {
          const locationsResponse = await clientAPI.getLocations({ city: requestData.from_location_name });
          const fetchedLocations = locationsResponse.data.results || locationsResponse.data || [];
          const existing = fetchedLocations.find(l =>
            l.city?.toLowerCase() === requestData.from_location_name.toLowerCase()
          );
          if (existing) {
            fromLocationId = existing.id;
          } else {
            throw new Error('Location non trouvée');
          }
        } catch (searchErr) {
          toast({
            title: 'Erreur',
            description: 'Impossible de créer ou trouver le lieu de départ. Veuillez vérifier le nom de la ville.',
            variant: 'destructive',
          });
          return;
        }
      }
    }

    // Créer ou trouver le lieu d'arrivée
    if (hasToLocationName && !toLocationId) {
      try {
        const response = await clientAPI.createLocation({
          city: requestData.to_location_name,
          country: 'Guinée',
        });
        toLocationId = response.data.id;
      } catch (err) {
        if (err.response?.data?.existing_location) {
          const existingLocation = err.response.data.existing_location;
          toast({
            title: 'Ville existante détectée',
            description: `La ville "${existingLocation.city}" existe déjà. Veuillez la sélectionner dans la liste déroulante.`,
            variant: 'default',
          });
          const response = await clientAPI.getLocations({});
          const allLocations = response.data.results || response.data || [];
          const groupedLocations = groupLocationsByCity(allLocations);
          setLocations(groupedLocations);
          setRequestData(prev => ({
            ...prev,
            to_location_id: existingLocation.id,
            to_location_name: '',
          }));
          return;
        }
        try {
          const locationsResponse = await clientAPI.getLocations({ city: requestData.to_location_name });
          const fetchedLocations = locationsResponse.data.results || locationsResponse.data || [];
          const existing = fetchedLocations.find(l =>
            l.city?.toLowerCase() === requestData.to_location_name.toLowerCase()
          );
          if (existing) {
            toLocationId = existing.id;
          } else {
            throw new Error('Location non trouvée');
          }
        } catch (searchErr) {
          toast({
            title: 'Erreur',
            description: 'Impossible de créer ou trouver le lieu d\'arrivée. Veuillez vérifier le nom de la ville.',
            variant: 'destructive',
          });
          return;
        }
      }
    }

    if (!fromLocationId || !toLocationId) {
      toast({
        title: 'Erreur',
        description: 'Les lieux de départ et d\'arrivée sont requis.',
        variant: 'destructive',
      });
      return;
    }

    if (fromLocationId === toLocationId) {
      toast({
        title: 'Erreur',
        description: 'Le lieu de départ et d\'arrivée doivent être différents.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setCreatingRequest(true);
      await adminAPI.createRequestForClient(clientId, {
        from_location: fromLocationId,
        to_location: toLocationId,
        preferred_pickup_date: requestData.preferred_pickup_date || null,
        preferred_delivery_date: requestData.preferred_delivery_date || null,
        declared_value: requestData.declared_value ? parseFloat(requestData.declared_value) : null,
        payment_terms: requestData.payment_terms || 'PREPAID',
        urgency_level: requestData.urgency_level || 'NORMAL',
        description: requestData.description || '',
        preferred_vehicle_type_code: requestData.preferred_vehicle_type && requestData.preferred_vehicle_type !== 'none' ? requestData.preferred_vehicle_type : null,
      });

      toast({
        title: 'Demande créée',
        description: 'La demande de transport a été créée avec succès au nom du client.',
      });
      setCreateDialogOpen(false);
      setRequestData({
        from_location_id: null,
        to_location_id: null,
        from_location_name: '',
        to_location_name: '',
        preferred_pickup_date: '',
        preferred_delivery_date: '',
        declared_value: '',
        payment_terms: 'PREPAID',
        urgency_level: 'NORMAL',
        description: '',
        preferred_vehicle_type: null,
      });
      // Recharger la liste des demandes
      await loadInitialData();
    } catch (error) {
      console.error('Erreur lors de la création de la demande:', error);
      let errorMessage = 'Erreur lors de la création de la demande.';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (typeof error.response.data === 'object') {
          const errors = Object.entries(error.response.data)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('; ');
          errorMessage = errors || JSON.stringify(error.response.data);
        }
      }
      toast({
        title: 'Erreur',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setCreatingRequest(false);
    }
  };

  // Filtrer les demandes localement (recherche)
  const filteredRequests = requests.filter(request => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const fromLocation = request.from_location_detail || request.from_location;
    const toLocation = request.to_location_detail || request.to_location;
    return (
      request.logiconnect_id?.toLowerCase().includes(searchLower) ||
      fromLocation?.city?.toLowerCase().includes(searchLower) ||
      fromLocation?.label?.toLowerCase().includes(searchLower) ||
      toLocation?.city?.toLowerCase().includes(searchLower) ||
      toLocation?.label?.toLowerCase().includes(searchLower) ||
      request.status?.toLowerCase().includes(searchLower)
    );
  });

  // Grouper les demandes par statut pour les colonnes Kanban
  const groupedRequests = useMemo(() => {
    return {
      DRAFT: filteredRequests.filter(r => r.status === 'DRAFT'),
      PUBLISHED: filteredRequests.filter(r => r.status === 'PUBLISHED' || r.status === 'ASSIGNED' || r.status === 'IN_TRANSIT'),
      COMPLETED: filteredRequests.filter(r => r.status === 'COMPLETED'),
      CANCELLED: filteredRequests.filter(r => r.status === 'CANCELLED'),
    };
  }, [filteredRequests]);

  // Fonction pour obtenir l'ID formaté
  const getRequestId = (request) => {
    if (request.logiconnect_id) {
      return `#${request.logiconnect_id}`;
    }
    const year = new Date(request.created_at).getFullYear();
    const shortId = request.id.slice(-3).toUpperCase();
    return `#TR-${year}-${shortId}`;
  };

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Fonction pour formater la monnaie
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0 GNF';
    return `${Number(amount).toLocaleString('fr-FR')} GNF`;
  };

  // Composant pour une carte de demande dans la vue Kanban
  const RequestCard = ({ request }) => {
    const urgencyColor = request.urgency_level === 'URGENT' ? 'bg-red-500' : 'bg-blue-500';
    const urgencyLabel = request.urgency_level === 'URGENT' ? 'URGENTE' : 'NORMALE';
    const fromLocation = request.from_location_detail || request.from_location;
    const toLocation = request.to_location_detail || request.to_location;

    return (
      <Card className="bg-card/80 border-border hover:border-primary/50 transition-all cursor-pointer">
        <CardContent className="p-4">
          {/* En-tête de la carte */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-muted-foreground">{getRequestId(request)}</span>
            <Badge className={`${urgencyColor} text-white text-xs`}>{urgencyLabel}</Badge>
          </div>

          {/* Trajet */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
              <span className="text-sm font-medium">DÉPART</span>
            </div>
            <p className="text-sm text-foreground ml-4">
              {fromLocation?.city || fromLocation?.label || 'N/A'}
            </p>

            <div className="flex items-center gap-2 mt-3">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="text-sm font-medium">DESTINATION</span>
            </div>
            <p className="text-sm text-foreground ml-4">
              {toLocation?.city || toLocation?.label || 'N/A'}
            </p>
          </div>

          {/* Date et valeur */}
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(request.created_at)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <DollarSign className="w-4 h-4" />
              <span>{formatCurrency(request.declared_value || 0)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/clients/${clientId}/requests/${request.id}`);
              }}
              size="sm"
              variant="outline"
              className="w-full"
            >
              <Eye className="w-4 h-4 mr-2" />
              Voir les détails →
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Configuration des colonnes pour le tableau
  const columns = [
    {
      id: 'id',
      header: 'ID',
      accessorKey: 'logiconnect_id',
      cell: ({ row }) => {
        const request = row.original;
        return (
          <div className="flex items-center gap-2">
            {request.logiconnect_id && (
              <LogiConnectIdBadge id={request.logiconnect_id} showLabel={false} size="sm" />
            )}
          </div>
        );
      },
    },
    {
      id: 'route',
      header: 'Itinéraire',
      accessorKey: 'route',
      cell: ({ row }) => {
        const request = row.original;
        const fromLocation = request.from_location_detail || request.from_location;
        const toLocation = request.to_location_detail || request.to_location;
        const fromCity = fromLocation?.city || fromLocation?.label || 'N/A';
        const toCity = toLocation?.city || toLocation?.label || 'N/A';
        return (
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            <div className="text-sm">
              <p className="font-medium">{fromCity}</p>
              <p className="text-muted-foreground text-xs">→ {toCity}</p>
            </div>
          </div>
        );
      },
    },
    {
      id: 'dates',
      header: 'Dates',
      accessorKey: 'dates',
      cell: ({ row }) => {
        const request = row.original;
        return (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <div>
              {request.preferred_pickup_date && (
                <p className="text-xs">Départ: {new Date(request.preferred_pickup_date).toLocaleDateString('fr-FR')}</p>
              )}
              {request.preferred_delivery_date && (
                <p className="text-xs">Arrivée: {new Date(request.preferred_delivery_date).toLocaleDateString('fr-FR')}</p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: 'value',
      header: 'Valeur déclarée',
      accessorKey: 'declared_value',
      cell: ({ row }) => {
        const request = row.original;
        return (
          <div className="flex items-center gap-1 text-sm">
            <DollarSign className="w-4 h-4 text-green-500" />
            <span>{request.declared_value ? Number(request.declared_value).toLocaleString('fr-FR') : '0'} GNF</span>
          </div>
        );
      },
    },
    {
      id: 'urgency',
      header: 'Urgence',
      accessorKey: 'urgency_level',
      cell: ({ row }) => {
        const request = row.original;
        const urgency = request.urgency_level || 'NORMAL';
        const urgencyConfig = {
          LOW: { label: 'Faible', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
          NORMAL: { label: 'Normale', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
          HIGH: { label: 'Élevée', className: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
          URGENT: { label: 'Urgente', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
        };
        const config = urgencyConfig[urgency] || urgencyConfig.NORMAL;
        return <Badge className={config.className}>{config.label}</Badge>;
      },
    },
    {
      id: 'status',
      header: 'Statut',
      accessorKey: 'status',
      cell: ({ row }) => {
        const request = row.original;
        // Normaliser le statut en majuscules
        const status = (request.status || 'DRAFT').toUpperCase();
        const statusConfig = {
          DRAFT: { label: 'Brouillon', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: FileText },
          PUBLISHED: { label: 'Publiée', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Package },
          ASSIGNED: { label: 'Assignée', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Package },
          IN_TRANSIT: { label: 'En transit', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
          IN_PROGRESS: { label: 'En cours', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
          COMPLETED: { label: 'Terminée', className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
          CANCELLED: { label: 'Annulée', className: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertCircle },
        };
        const config = statusConfig[status] || statusConfig.DRAFT;
        const Icon = config.icon;
        return (
          <Badge className={config.className}>
            <Icon className="w-3 h-3 mr-1" />
            {config.label}
          </Badge>
        );
      },
    },
    {
      id: 'created_at',
      header: 'Créée le',
      accessorKey: 'created_at',
      cell: ({ row }) => {
        const request = row.original;
        return (
          <span className="text-sm text-muted-foreground">
            {request.created_at ? new Date(request.created_at).toLocaleDateString('fr-FR') : 'N/A'}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const request = row.original;
        // Normaliser le statut en majuscules pour éviter les problèmes de casse
        const status = (request.status || 'DRAFT').toUpperCase();

        // Déterminer quelles actions sont disponibles selon le statut
        const hasActions = status === 'DRAFT' || status === 'PUBLISHED' || status === 'ASSIGNED' || status === 'IN_TRANSIT' || status === 'CANCELLED';

        return (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/admin/clients/${clientId}/requests/${request.id}`)}
            >
              <Eye className="w-4 h-4 mr-2" />
              Voir
            </Button>
            {hasActions && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {status === 'DRAFT' && (
                    <>
                      <DropdownMenuItem
                        onClick={async () => {
                          try {
                            await adminAPI.updateRequest(request.id, { status: 'PUBLISHED' });
                            toast({
                              title: 'Demande publiée',
                              description: 'La demande a été publiée avec succès.',
                            });
                            await loadInitialData();
                          } catch (error) {
                            toast({
                              title: 'Erreur',
                              description: error.response?.data?.detail || 'Erreur lors de la publication.',
                              variant: 'destructive',
                            });
                          }
                        }}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Publier
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedRequestForAction(request);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </>
                  )}
                  {status === 'PUBLISHED' && (
                    <>
                      <DropdownMenuItem
                        onClick={async () => {
                          try {
                            await adminAPI.updateRequest(request.id, { status: 'DRAFT' });
                            toast({
                              title: 'Demande dépubliée',
                              description: 'La demande a été dépubliée avec succès.',
                            });
                            await loadInitialData();
                          } catch (error) {
                            toast({
                              title: 'Erreur',
                              description: error.response?.data?.detail || 'Erreur lors de la dépublication.',
                              variant: 'destructive',
                            });
                          }
                        }}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Dépublier
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={async () => {
                          try {
                            await adminAPI.updateRequest(request.id, { status: 'CANCELLED' });
                            toast({
                              title: 'Demande annulée',
                              description: 'La demande a été annulée avec succès.',
                            });
                            await loadInitialData();
                          } catch (error) {
                            toast({
                              title: 'Erreur',
                              description: error.response?.data?.detail || 'Erreur lors de l\'annulation.',
                              variant: 'destructive',
                            });
                          }
                        }}
                        className="text-destructive"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Annuler
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedRequestForAction(request);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </>
                  )}
                  {(status === 'ASSIGNED' || status === 'IN_TRANSIT') && (
                    <DropdownMenuItem
                      onClick={async () => {
                        try {
                          await adminAPI.updateRequest(request.id, { status: 'CANCELLED' });
                          toast({
                            title: 'Demande annulée',
                            description: 'La demande a été annulée avec succès.',
                          });
                          await loadInitialData();
                        } catch (error) {
                          toast({
                            title: 'Erreur',
                            description: error.response?.data?.detail || 'Erreur lors de l\'annulation.',
                            variant: 'destructive',
                          });
                        }
                      }}
                      className="text-destructive"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Annuler
                    </DropdownMenuItem>
                  )}
                  {status === 'CANCELLED' && (
                    <DropdownMenuItem
                      onClick={() => {
                        setSelectedRequestForAction(request);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Supprimer
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-teal-500" />
            Demandes de transport
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Bascule vue */}
            <div className="flex items-center gap-1 bg-card/50 border border-border rounded-lg p-1">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="h-8"
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="h-8"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={() => setCreateDialogOpen(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-primary font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer une demande
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Statistiques */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="bg-card/50 border-border rounded-lg">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-yellow-500/20">
                <FolderOpen className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">TOTAL</p>
                <p className="text-2xl font-bold">{statistics.total}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border rounded-lg">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/20">
                <Rocket className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">PUBLIÉES</p>
                <p className="text-2xl font-bold">{statistics.published}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border rounded-lg">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-600/20">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">TERMINÉES</p>
                <p className="text-2xl font-bold">{statistics.completed}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border rounded-lg">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-gray-500/20">
                <FileText className="w-6 h-6 text-gray-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">BROUILLONS</p>
                <p className="text-2xl font-bold">{statistics.draft}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border rounded-lg">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-red-500/20">
                <XCircle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">ANNULÉES</p>
                <p className="text-2xl font-bold">{statistics.cancelled}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtres */}
        {showFilters && (
          <div className="bg-muted/30 rounded-lg p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Rechercher par ID, ville..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="DRAFT">Brouillon</option>
                  <option value="PUBLISHED">Publiée</option>
                  <option value="IN_PROGRESS">En cours</option>
                  <option value="COMPLETED">Terminée</option>
                  <option value="CANCELLED">Annulée</option>
                </select>
                <select
                  value={urgencyFilter}
                  onChange={(e) => setUrgencyFilter(e.target.value)}
                  className="px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm"
                >
                  <option value="all">Tous les niveaux</option>
                  <option value="LOW">Faible</option>
                  <option value="NORMAL">Normale</option>
                  <option value="HIGH">Élevée</option>
                  <option value="URGENT">Urgente</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Zone principale - Vue Kanban ou Liste */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredRequests.length === 0 ? (
          <Card className="bg-card/50 border-border">
            <CardContent className="p-12 text-center">
              <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Aucune demande</h3>
              <p className="text-muted-foreground mb-4">Aucune demande de transport trouvée.</p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="w-5 h-5 mr-2" />
                Créer une demande
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Colonne BROUILLON */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold">BROUILLON ({groupedRequests.DRAFT.length})</h2>
                <MoreVertical className="w-4 h-4 text-muted-foreground cursor-pointer" />
              </div>
              <div className="space-y-4">
                {groupedRequests.DRAFT.map((request) => (
                  <RequestCard key={request.id} request={request} />
                ))}
                {/* Carte NOUVELLE DEMANDE */}
                <Card
                  className="bg-card/30 border-2 border-dashed border-primary/50 hover:border-primary cursor-pointer transition-colors"
                  onClick={() => setCreateDialogOpen(true)}
                >
                  <CardContent className="p-6 flex flex-col items-center justify-center min-h-[200px] text-center">
                    <Plus className="w-12 h-12 text-primary mb-4" />
                    <p className="text-lg font-semibold text-primary">NOUVELLE DEMANDE</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Colonne PUBLIÉE */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold">PUBLIÉE ({groupedRequests.PUBLISHED.length})</h2>
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                </div>
                <MoreVertical className="w-4 h-4 text-muted-foreground cursor-pointer" />
              </div>
              <div className="space-y-4">
                {groupedRequests.PUBLISHED.map((request) => (
                  <RequestCard key={request.id} request={request} />
                ))}
              </div>
            </div>

            {/* Colonne TERMINÉE */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold">TERMINÉE ({groupedRequests.COMPLETED.length})</h2>
                <MoreVertical className="w-4 h-4 text-muted-foreground cursor-pointer" />
              </div>
              <div className="space-y-4">
                {groupedRequests.COMPLETED.map((request) => (
                  <RequestCard key={request.id} request={request} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Vue liste (ancienne vue avec DataTable) */
          <div className="min-h-[400px]">
            <DataTable
              data={filteredRequests}
              columns={columns}
              loading={loading}
              searchable={false}
              pageSize={10}
            />
          </div>
        )}
      </CardContent>

      {/* Dialog de création de demande */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Créer une demande au nom du client</DialogTitle>
            <DialogDescription>
              Créez une nouvelle demande de transport au compte de ce client. La demande sera créée en brouillon.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lieu de départ</Label>
                <Select
                  value={requestData.from_location_id || 'new'}
                  onValueChange={(value) => {
                    if (value === 'new') {
                      setRequestData(prev => ({ ...prev, from_location_id: null, from_location_name: '' }));
                    } else {
                      setRequestData(prev => ({ ...prev, from_location_id: value, from_location_name: '' }));
                    }
                  }}
                  disabled={loadingLocations}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner ou créer un lieu" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">➕ Créer un nouveau lieu</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.label || location.city || location.id.slice(-8)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(!requestData.from_location_id || requestData.from_location_id === 'new') && (
                  <Input
                    placeholder="Entrez le nom de la ville (ex: Conakry, Kankan...)"
                    value={requestData.from_location_name}
                    onChange={(e) => setRequestData(prev => ({ ...prev, from_location_name: e.target.value }))}
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label>Lieu d'arrivée</Label>
                <Select
                  value={requestData.to_location_id || 'new'}
                  onValueChange={(value) => {
                    if (value === 'new') {
                      setRequestData(prev => ({ ...prev, to_location_id: null, to_location_name: '' }));
                    } else {
                      setRequestData(prev => ({ ...prev, to_location_id: value, to_location_name: '' }));
                    }
                  }}
                  disabled={loadingLocations}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner ou créer un lieu" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">➕ Créer un nouveau lieu</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.label || location.city || location.id.slice(-8)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(!requestData.to_location_id || requestData.to_location_id === 'new') && (
                  <Input
                    placeholder="Entrez le nom de la ville (ex: Conakry, Kankan...)"
                    value={requestData.to_location_name}
                    onChange={(e) => setRequestData(prev => ({ ...prev, to_location_name: e.target.value }))}
                  />
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date d'enlèvement prévue</Label>
                <Input
                  type="date"
                  value={requestData.preferred_pickup_date}
                  onChange={(e) => setRequestData(prev => ({ ...prev, preferred_pickup_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Date de livraison prévue</Label>
                <Input
                  type="date"
                  value={requestData.preferred_delivery_date}
                  onChange={(e) => setRequestData(prev => ({ ...prev, preferred_delivery_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Valeur déclarée (GNF)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={requestData.declared_value}
                  onChange={(e) => setRequestData(prev => ({ ...prev, declared_value: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Urgence</Label>
                <Select value={requestData.urgency_level} onValueChange={(value) => setRequestData(prev => ({ ...prev, urgency_level: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Faible</SelectItem>
                    <SelectItem value="NORMAL">Normale</SelectItem>
                    <SelectItem value="HIGH">Élevée</SelectItem>
                    <SelectItem value="URGENT">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Conditions de paiement</Label>
              <Select value={requestData.payment_terms} onValueChange={(value) => setRequestData(prev => ({ ...prev, payment_terms: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PREPAID">Prépaiement</SelectItem>
                  <SelectItem value="COD">Paiement à la livraison</SelectItem>
                  <SelectItem value="POSTPAID">Paiement différé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Type de véhicule souhaité (optionnel)</Label>
              <Select
                value={requestData.preferred_vehicle_type || 'none'}
                onValueChange={(value) => setRequestData(prev => ({ ...prev, preferred_vehicle_type: value === 'none' ? null : value }))}
                disabled={loadingVehicleTypes}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingVehicleTypes ? "Chargement..." : "Sélectionnez un type de véhicule (optionnel)"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune préférence</SelectItem>
                  {vehicleTypes.map((vehicleType) => (
                    <SelectItem key={vehicleType.code} value={vehicleType.code}>
                      {vehicleType.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description (optionnel)</Label>
              <Textarea
                placeholder="Décrivez la demande..."
                value={requestData.description}
                onChange={(e) => setRequestData(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={creatingRequest}>
              Annuler
            </Button>
            <Button onClick={handleCreateRequest} disabled={creatingRequest}>
              {creatingRequest ? 'Création...' : 'Créer la demande'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Êtes-vous sûr ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action supprimera définitivement la demande. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (selectedRequestForAction) {
                  try {
                    await adminAPI.deleteRequest(selectedRequestForAction.id);
                    toast({
                      title: 'Demande supprimée',
                      description: 'La demande a été supprimée avec succès.',
                    });
                    setDeleteDialogOpen(false);
                    setSelectedRequestForAction(null);
                    await loadInitialData();
                  } catch (error) {
                    toast({
                      title: 'Erreur',
                      description: error.response?.data?.detail || 'Erreur lors de la suppression.',
                      variant: 'destructive',
                    });
                  }
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default RequestManagement;
